import type { HTMLAttributes } from "react";

/**
 * Subtle shimmer skeleton block. Use to take up the same shape as the
 * eventual content so the layout doesn't jump on load. Keeps the page
 * calm - no spinning loaders, no big "Loading..." text.
 */
export function Skeleton({
  className = "",
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-md bg-[var(--color-elevated)]",
        "before:absolute before:inset-0 before:-translate-x-full",
        "before:bg-gradient-to-r before:from-transparent before:via-white/[0.04] before:to-transparent",
        "before:animate-[skeleton-shimmer_1.6s_infinite]",
        className,
      ].join(" ")}
      aria-hidden="true"
      {...rest}
    />
  );
}

/** Standard placeholder card: header + a few rows. */
export function SkeletonCard({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-3" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}

/** Grid of placeholder cards for stat-row / overview layouts. */
export function SkeletonStatGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="h-3.5 w-3.5 rounded" />
          </div>
          <Skeleton className="h-6 w-16 mt-1.5" />
        </div>
      ))}
    </div>
  );
}

/** Generic vertical list of placeholder rows for events / activity feeds. */
export function SkeletonList({ rows = 6 }: { rows?: number }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="p-4 flex items-start gap-3">
          <Skeleton className="h-7 w-7 rounded-md shrink-0" />
          <div className="flex-1 space-y-2 min-w-0">
            <Skeleton className="h-3 w-2/5" />
            <Skeleton className="h-2.5 w-3/4" />
          </div>
          <Skeleton className="h-3 w-20 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Table-row placeholder, sized to match standard tables. */
export function SkeletonTable({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
      <div className="px-5 py-3 border-b border-[var(--color-border)]">
        <Skeleton className="h-3 w-32" />
      </div>
      <div className="divide-y divide-[var(--color-border)]">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="px-5 py-3 flex items-center gap-4">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton
                key={c}
                className="h-3 flex-1"
                style={{ maxWidth: `${100 - c * 10}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
